/**
 * Базовый конверт моста web ↔ native. Чистый TS без зависимостей —
 * этот слой можно опубликовать как контракт для отдельного web-репозитория.
 *
 * web → native: { v, action, payload, id? }
 * native → web: { v, event, payload, correlationId?, error? }
 */
export const PROTOCOL_VERSION = 1 as const;
export type ProtocolVersion = typeof PROTOCOL_VERSION;

/** Сообщение из web в native. `id` присутствует, если web ждёт ответ (RPC). */
export interface WebMessage<A extends string = string, P = unknown> {
  v: ProtocolVersion;
  action: A;
  payload: P;
  id?: string;
}

/** Ошибка ответа на RPC. */
export interface BridgeError {
  code: string;
  message: string;
}

/** Сообщение из native в web. `correlationId` эхо-ит `WebMessage.id` для ответа на RPC. */
export interface NativeMessage<E extends string = string, P = unknown> {
  v: ProtocolVersion;
  event: E;
  payload: P;
  correlationId?: string;
  error?: BridgeError;
}

/** Имя события-ответа на RPC. Web сопоставляет ответ по `correlationId`, не по имени. */
export const RPC_RESPONSE_EVENT = 'rpc.response' as const;
export type RpcResponseEvent = typeof RPC_RESPONSE_EVENT;

export const BRIDGE_ERROR_CODES = {
  INVALID_MESSAGE: 'INVALID_MESSAGE',
  UNSUPPORTED_VERSION: 'UNSUPPORTED_VERSION',
  UNKNOWN_ACTION: 'UNKNOWN_ACTION',
  HANDLER_FAILED: 'HANDLER_FAILED'
} as const;
export type BridgeErrorCode =
  (typeof BRIDGE_ERROR_CODES)[keyof typeof BRIDGE_ERROR_CODES];

/** Сконструировать событие native → web (с опциональной корреляцией). */
export function nativeEvent<E extends string, P>(
  event: E,
  payload: P,
  correlationId?: string
): NativeMessage<E, P> {
  const message: NativeMessage<E, P> = { v: PROTOCOL_VERSION, event, payload };
  if (correlationId !== undefined) message.correlationId = correlationId;
  return message;
}

/** Успешный ответ на RPC. */
export function rpcResult(
  correlationId: string,
  payload: unknown
): NativeMessage<RpcResponseEvent> {
  return {
    v: PROTOCOL_VERSION,
    event: RPC_RESPONSE_EVENT,
    payload,
    correlationId
  };
}

/** Ответ-ошибка на RPC. */
export function rpcError(
  correlationId: string,
  error: BridgeError
): NativeMessage<RpcResponseEvent, null> {
  return {
    v: PROTOCOL_VERSION,
    event: RPC_RESPONSE_EVENT,
    payload: null,
    correlationId,
    error
  };
}
